import React, { useState, useRef } from 'react';
import { Manga } from '../types/manga';
import {
  getFlagUrl,
  getStatusBadge,
  isUpdatedToday,
  formatImageUrl,
  DEFAULT_COVER_PLACEHOLDER,
  getLatestReleaseAt,
  formatRelativeThai,
} from '../utils/helpers';
import { Minus, Plus, Pencil, ExternalLink, GripVertical, ChevronUp, ChevronDown, Clock } from 'lucide-react';

interface MangaCardProps {
  manga: Manga;
  index?: number;
  isSortMode: boolean;
  isDragging?: boolean;
  isDropTarget?: boolean;
  onQuickPlus: (id: string) => void;
  onQuickMinus: (id: string) => void;
  onEdit: (manga: Manga) => void;
  onOpenSourceDetails: (manga: Manga) => void;
  onMoveUp?: (id: string) => void;
  onMoveDown?: (id: string) => void;
  onDragStart?: (e: React.DragEvent, id: string) => void;
  onDragOver?: (e: React.DragEvent, id: string) => void;
  onDrop?: (e: React.DragEvent, id: string) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onTouchStartDrag?: (e: React.TouchEvent, manga: Manga) => void;
  onStartSortMode?: () => void;
}

// Memoized: during a sort drag only the cards whose drag/drop state changes re-render
export const MangaCard = React.memo<MangaCardProps>(function MangaCard({
  manga,
  index = 0,
  isSortMode,
  isDragging = false,
  isDropTarget = false,
  onQuickPlus,
  onQuickMinus,
  onEdit,
  onOpenSourceDetails,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onTouchStartDrag,
  onStartSortMode,
}) {
  const [imgError, setImgError] = useState(false);
  const [isPressingPlus, setIsPressingPlus] = useState(false);
  const [isPressingMinus, setIsPressingMinus] = useState(false);

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const flagUrl = getFlagUrl(manga.nationality);
  const updatedToday = isUpdatedToday(manga);
  const statusInfo = getStatusBadge(manga);

  const readCh = Number(manga.readCh) || 0;
  const currentCh = Number(manga.currentCh) || 0;
  const progressPercent = currentCh > 0 ? Math.min(100, Math.round((readCh / currentCh) * 100)) : 100;

  const releaseAt = getLatestReleaseAt(manga);
  const isFreshRelease = releaseAt !== null && Date.now() - releaseAt < 24 * 60 * 60 * 1000;

  const coverSrc = imgError
    ? DEFAULT_COVER_PLACEHOLDER
    : formatImageUrl(manga.imageUrl);

  // Quick buttons handlers
  const handlePlus = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPressingPlus(true);
    setTimeout(() => setIsPressingPlus(false), 200);
    onQuickPlus(manga.id);
  };

  const handleMinus = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readCh <= 0) return;
    setIsPressingMinus(true);
    setTimeout(() => setIsPressingMinus(false), 200);
    onQuickMinus(manga.id);
  };

  // Touch Handling for iOS-style Long Press & Drag
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

    // Clear any existing timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    if (!isSortMode && onStartSortMode) {
      // Long press (450ms) to trigger sort mode from normal view
      longPressTimerRef.current = setTimeout(() => {
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate(30);
        }
        onStartSortMode();
        onTouchStartDrag?.(e, manga);
      }, 450);
    } else if (isSortMode && onTouchStartDrag) {
      // In sort mode: holding for 120ms initiates drag
      longPressTimerRef.current = setTimeout(() => {
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate(20);
        }
        onTouchStartDrag(e, manga);
      }, 120);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartPosRef.current && longPressTimerRef.current) {
      const touch = e.touches[0];
      if (touch) {
        const dx = Math.abs(touch.clientX - touchStartPosRef.current.x);
        const dy = Math.abs(touch.clientY - touchStartPosRef.current.y);
        // Cancel timer if finger moves significantly before threshold
        if (dx > 8 || dy > 8) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Choose styling based on states
  let cardClass = '';
  if (isDragging) {
    cardClass = 'opacity-30 scale-95';
  } else if (isDropTarget) {
    cardClass = 'ios-drop-target';
  } else if (isSortMode) {
    const jiggleIndex = index % 4;
    cardClass = `ios-jiggle-${jiggleIndex} cursor-grab active:cursor-grabbing outline-dashed outline-1 outline-offset-2 outline-amber-400/60`;
  }

  const actionClass =
    'flex items-center justify-center text-zinc-400 hover:text-zinc-50 hover:bg-white/[0.06] disabled:opacity-30 disabled:pointer-events-none transition-colors';

  return (
    <div
      data-manga-id={manga.id}
      draggable={isSortMode}
      onDragStart={(e) => onDragStart && onDragStart(e, manga.id)}
      onDragOver={(e) => onDragOver && onDragOver(e, manga.id)}
      onDrop={(e) => onDrop && onDrop(e, manga.id)}
      onDragEnd={(e) => onDragEnd && onDragEnd(e)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onContextMenu={(e) => {
        if (isSortMode) e.preventDefault();
      }}
      className={`group relative flex flex-col w-full rounded-lg sm:rounded-xl transition-all duration-150 select-none ${cardClass}`}
    >
      {/* Cover Image Container (Aspect 3:4) */}
      <div className="relative aspect-[3/4] w-full rounded-lg sm:rounded-xl bg-zinc-900 overflow-hidden ring-1 ring-inset ring-white/[0.06]">
        <button
          type="button"
          disabled={isSortMode}
          onClick={() => !isSortMode && onOpenSourceDetails(manga)}
          title={isSortMode ? 'กดค้างหรือลากเพื่อย้ายตำแหน่ง' : 'แตะเพื่อดูแหล่งอ่านและรายละเอียด'}
          className={`w-full h-full block focus:outline-none relative ${
            isSortMode ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
        >
          <img
            src={coverSrc}
            alt={manga.title}
            onError={() => setImgError(true)}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-300 pointer-events-none"
          />
        </button>

        {/* Flag on top-right */}
        {flagUrl && (
          <img
            src={flagUrl}
            alt={manga.nationality}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="absolute top-1.5 right-1.5 z-10 w-4 h-3 sm:w-[18px] sm:h-[13px] rounded-[2px] object-cover shadow ring-1 ring-black/30 pointer-events-none"
          />
        )}

        {/* Updated today */}
        {updatedToday && !isSortMode && (
          <span className="absolute top-1.5 left-1.5 z-10 px-1.5 py-[3px] rounded-md bg-rose-500 text-white text-[8.5px] sm:text-[10px] font-semibold leading-none shadow pointer-events-none">
            ใหม่วันนี้
          </span>
        )}

        {/* iOS Sort Controls (if in sort mode) */}
        {isSortMode && (
          <div className="absolute top-1.5 left-1.5 z-20 flex flex-col gap-1">
            <div
              className="bg-amber-400 text-zinc-950 p-1 rounded-md shadow-md flex items-center justify-center cursor-grab active:cursor-grabbing"
              title="ลากเพื่อย้าย"
            >
              <GripVertical className="w-3 h-3" />
            </div>
            {onMoveUp && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp(manga.id);
                }}
                className="bg-zinc-950/80 backdrop-blur hover:bg-amber-400 text-zinc-100 hover:text-zinc-950 p-1 rounded-md shadow ring-1 ring-white/10 active:scale-90 transition"
                title="ย้ายขึ้น"
              >
                <ChevronUp className="w-3 h-3" />
              </button>
            )}
            {onMoveDown && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown(manga.id);
                }}
                className="bg-zinc-950/80 backdrop-blur hover:bg-amber-400 text-zinc-100 hover:text-zinc-950 p-1 rounded-md shadow ring-1 ring-white/10 active:scale-90 transition"
                title="ย้ายลง"
              >
                <ChevronDown className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {/* Reading progress */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-black/50">
          <div
            className={`h-full transition-all duration-300 ${
              statusInfo.isCaughtUp ? 'bg-emerald-400' : 'bg-amber-400'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Card Content */}
      <div className="pt-2 px-0.5 flex flex-col flex-grow">
        <h4
          title={manga.title}
          className="font-medium text-[11px] sm:text-[13px] text-zinc-100 line-clamp-2 leading-snug min-h-[2.75em]"
        >
          {manga.title}
        </h4>

        {/* Chapter numbers & status */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mt-1">
          <span className="text-zinc-300 text-[10px] sm:text-xs font-medium tabular-nums whitespace-nowrap">
            {readCh}
            <span className="text-zinc-600"> / {currentCh}</span>
          </span>
          <span
            className={`px-1.5 py-[3px] rounded text-[8.5px] sm:text-[10px] font-medium leading-none whitespace-nowrap ${statusInfo.badgeClass}`}
          >
            {statusInfo.label}
          </span>
        </div>

        {/* Source + release time of the newest chapter on the fastest site */}
        <div className="mt-1.5 space-y-0.5 text-[9.5px] sm:text-[11px] text-zinc-500">
          {manga.latestSourceName &&
            (manga.latestSourceUrl ? (
              <a
                href={manga.latestSourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="เปิดหน้าเว็บอ่าน"
                className="flex items-center gap-1 hover:text-zinc-200 transition-colors min-w-0"
              >
                <span className="truncate">{manga.latestSourceName}</span>
                <ExternalLink className="w-2.5 h-2.5 shrink-0" />
              </a>
            ) : (
              <div className="truncate">{manga.latestSourceName}</div>
            ))}
          {releaseAt !== null && (
            <div
              title={`ตอน ${currentCh} ออกเมื่อ ${new Date(releaseAt).toLocaleString('th-TH')}`}
              className={`flex items-center gap-1 ${isFreshRelease ? 'text-rose-300' : ''}`}
            >
              <Clock className="w-2.5 h-2.5 shrink-0" />
              <span className="truncate">{formatRelativeThai(releaseAt)}</span>
            </div>
          )}
        </div>

        {/* Action Buttons: Minus, Edit, Plus */}
        <div className={`mt-auto pt-2 transition-opacity ${isSortMode ? 'opacity-30 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-3 h-7 sm:h-8 rounded-lg bg-white/[0.04] ring-1 ring-inset ring-white/[0.06] overflow-hidden divide-x divide-white/[0.06]">
            <button
              type="button"
              onClick={handleMinus}
              disabled={readCh <= 0 || isSortMode}
              title="ลดตอนที่อ่านไปแล้ว (-1)"
              className={`${actionClass} ${isPressingMinus ? 'bg-white/10' : ''}`}
            >
              <Minus className="w-3.5 h-3.5" />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(manga);
              }}
              disabled={isSortMode}
              title="แก้ไขข้อมูลเรื่องนี้"
              className={actionClass}
            >
              <Pencil className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            </button>

            <button
              type="button"
              onClick={handlePlus}
              disabled={isSortMode}
              title="เพิ่มตอนที่อ่านไปแล้ว (+1)"
              className={`flex items-center justify-center text-violet-300 hover:text-violet-200 hover:bg-violet-500/15 disabled:opacity-30 disabled:pointer-events-none transition-colors ${
                isPressingPlus ? 'bg-violet-500/25' : ''
              }`}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
